/* eslint-disable security/detect-unsafe-regex */
// Shared browser- and server-safe Markdown renderer.
import { load as loadYaml } from 'js-yaml';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import remarkParse from 'remark-parse';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import type { BlockContent, DefinitionContent, PhrasingContent, Root, Paragraph, Literal } from 'mdast';
import type { Transformer } from 'unified';
import { unified } from 'unified';
import { defaultSchema } from 'hast-util-sanitize';
import type { Schema } from 'hast-util-sanitize';
import deepmerge from 'deepmerge';
import type { ContainerDirective, LeafDirective, TextDirective } from 'mdast-util-directive';
import rehypeParse from 'rehype-parse';
import rehypeRemark from 'rehype-remark';
import { toJsxRuntime, type Components } from 'hast-util-to-jsx-runtime';
import type { Element, Root as HastRoot } from 'hast';
import { Fragment, type ReactElement, useMemo } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import remarkStringify from 'remark-stringify';
import mammoth from 'mammoth';
import stripMarkdown from 'strip-markdown';

export type DirectiveData = Required<Root>['data'];
export type DirectiveAttributes = Record<string, string | null | undefined>;
export type DirectiveChildren = PhrasingContent[] | (BlockContent | DefinitionContent)[];

export type DirectiveNode = (TextDirective | LeafDirective | ContainerDirective) & {
	data: DirectiveData;
	attributes: DirectiveAttributes;
	children: DirectiveChildren;
};

export type NodeWithChildren = {
	children: PhrasingContent[];
};

export type RemarkDirectiveOptions = {
	overrides?: {
		[name: string]: RemarkDirectiveHandler;
	};
	stripMarkdown?: boolean;
	/**
	 * Merged into the sanitize schema used by `markdownToHtml`. Lets a caller keep attributes the
	 * shared schema drops, without widening what the web and PDF outputs allow. The docx export uses
	 * it to keep the marker attribute it puts on nodes it will remove later.
	 */
	sanitizeSchemaExtension?: Schema;
};

export type RemarkDirectiveHandler = (
	node: DirectiveNode,
	options?: RemarkDirectiveOptions,
	index?: number,
	parent?: NodeWithChildren,
) => void;

type Frontmatter = Record<string, unknown>;

/**
 * Reconstructs a directive's label as the plain text it was authored as.
 *
 * A label is not always a single text node. Any colon-prefixed word inside one parses as a *nested*
 * directive — the page range in a citation like `2014;35:614-623`, or a colon in a filename — which
 * splits the label into several children. Handlers that only looked at `children[0]` saw a truncated
 * string and rejected the directive, failing the whole document; five published articles were in that
 * state. Inline markup (`**bold**`, links) splits it too.
 *
 * Nested directives are rebuilt from their source so nothing is silently dropped. Emphasis markers
 * are not: the result is plain text, used as a URL, an id, or tooltip content.
 */
export function directiveLabelToText(children: unknown[]): string {
	return children.map(nodeToLabelText).join('');
}

function nodeToLabelText(node: unknown): string {
	const candidate = node as { type?: string; value?: string; name?: string; children?: unknown[] } & {
		attributes?: DirectiveAttributes;
	};

	if (typeof candidate.value === 'string') {
		return candidate.value;
	}

	if (nodeIsTextDirective(node) || nodeIsLeafDirective(node) || nodeIsContainerDirective(node)) {
		const label = candidate.children?.length ? `[${directiveLabelToText(candidate.children)}]` : '';
		const attributes = Object.entries(candidate.attributes ?? {})
			.map(([key, value]) => (value ? `${key}="${value}"` : key))
			.join(' ');
		return `:${candidate.name}${label}${attributes ? `{${attributes}}` : ''}`;
	}

	if (candidate.type === 'break') {
		return ' ';
	}

	return candidate.children ? directiveLabelToText(candidate.children) : '';
}

function parseYamlFrontmatter(markdown: string): { data: Frontmatter; content: string } {
	const openingDelimiter = /^---([^\r\n]*)\r?\n/.exec(markdown);
	if (!openingDelimiter) {
		return { data: {}, content: markdown };
	}

	if (openingDelimiter[1].trim() !== '') {
		throw new Error(`Unsupported frontmatter language: ${openingDelimiter[1].trim()}. Only YAML is supported.`);
	}

	const frontmatterStart = openingDelimiter[0].length;
	const closingDelimiter = /\r?\n---[ \t]*(?:\r?\n|$)/g;
	closingDelimiter.lastIndex = frontmatterStart;
	const closingMatch = closingDelimiter.exec(markdown);
	if (!closingMatch || closingMatch.index < frontmatterStart) {
		throw new Error('YAML frontmatter is missing its closing delimiter.');
	}

	const parsed = loadYaml(markdown.slice(frontmatterStart, closingMatch.index));
	if (parsed === undefined || parsed === null) {
		return { data: {}, content: markdown.slice(closingMatch.index + closingMatch[0].length) };
	}
	if (typeof parsed !== 'object' || Array.isArray(parsed)) {
		throw new Error('YAML frontmatter must be a mapping.');
	}

	return {
		data: parsed as Frontmatter,
		content: markdown.slice(closingMatch.index + closingMatch[0].length),
	};
}

export default class MarkdownService {
	public async markdownToReact(markdownToConvert: string, components?: Partial<Components>) {
		const { frontmatter, html } = await this.markdownToHtml(markdownToConvert);

		const processor = unified().use(rehypeParse, { fragment: true });
		processor.Compiler = ((tree: unknown) =>
			toJsxRuntime(tree as HastRoot, {
				Fragment,
				jsx,
				jsxs,
				components,
			})) as never;

		const result = await processor.process(html);

		return {
			frontmatter,
			reactElement: result.result as ReactElement,
		};
	}

	public async docxToMarkdown(file: File) {
		return new Promise<string>((resolve, reject) => {
			const fileReader = new FileReader();
			fileReader.onload = (e) => {
				if (e.target?.result) {
					const arrayBuffer = e.target.result as ArrayBuffer;
					mammoth
						.convertToHtml({ arrayBuffer })
						.then((result) => {
							return this.htmlToMarkdown(result.value);
						})
						.then(resolve)
						.catch(reject);
				} else {
					// Without this the promise never settles and the caller waits forever.
					reject(new Error('Failed to read file: no content'));
				}
			};
			fileReader.onerror = () => {
				reject(new Error('Failed to read file'));
			};
			fileReader.readAsArrayBuffer(file);
		});
	}

	public async htmlToMarkdown(htmlToConvert: string) {
		const result = await unified()
			.use(rehypeParse) // Parse HTML to a syntax tree
			.use(rehypeRemark, {
				handlers: {
					// Drop the image — we don't want to support it. Returning nothing is what excludes it:
					// splicing it out of its parent instead shortened the child list midway through the
					// walk, so the siblings after it were skipped and their text was lost too.
					img: () => undefined,
				},
			}) // Turn HTML syntax tree to markdown syntax tree
			// rehype-remark produces `table` and `delete` nodes, which plain remarkStringify cannot
			// serialize — without this any HTML table failed the whole conversion, and importing a Word
			// document containing one threw rather than importing.
			.use(remarkGfm)
			.use(remarkStringify) // Serialize HTML syntax tree
			.process(htmlToConvert);
		return result.value as string;
	}

	public async markdownToPlainText(markdownToConvert: string, options?: RemarkDirectiveOptions) {
		const { data: frontmatter, content: markdown } = parseYamlFrontmatter(markdownToConvert);
		const result = await unified()
			.use(remarkParse)
			.use(remarkGfm)
			.use(remarkDirective)
			.use(MarkdownService.remarkPlugin, { ...options, stripMarkdown: true })
			.use(stripMarkdown)
			.use(remarkStringify)
			.process(markdown);

		return {
			frontmatter,
			markdown,
			text: result.toString(),
		};
	}

	public async markdownToHtml(markdownToConvert: string, options?: RemarkDirectiveOptions) {
		const { data: frontmatter, content: markdown } = parseYamlFrontmatter(markdownToConvert);

		const schema = deepmerge(defaultSchema, {
			tagNames: ['iframe', 'fieldset', 'label', 'legend', 'audio', 'source'],
			clobberPrefix: '',
			ancestors: {
				source: ['audio'],
			},
			attributes: {
				iframe: [
					['src', /^https:\/\/www\.youtube\.com\/embed\//, /https:\/\/player\.vimeo\.com\/video\//],
					'title',
					'width',
					'height',
					'frameBorder',
					'allow',
					'allowFullScreen',
				],
				audio: ['controls'],
				source: ['src', 'type'],
				a: ['class', 'data-tippy-content', 'data-article-id', 'data-quiz-id', 'data-resource-id', 'target'],
				// Only our own directive handlers can produce a div — raw HTML in the markdown is dropped
				// before sanitization — so this just lets the question wrapper keep its class.
				div: ['class'],
				// Without this the quote directive's `pull-quote` class is stripped and its styling never applies.
				blockquote: ['class'],
				fieldset: ['id'],
				label: ['for'],
			},
			protocols: {
				src: ['blob'], // this allows for URL.createObjectURL()
			},
		} as Schema);

		// `input` is assigned rather than merged. deepmerge concatenates, which would leave the defaults'
		// GFM task-list entries (`['disabled', true]`, `['type', 'checkbox']`) ahead of ours — and the
		// first matching entry wins, so every quiz option came out as a disabled checkbox. Task lists
		// still render correctly, since `checkbox`, `disabled` and `checked` remain permitted values.
		schema.attributes = {
			...schema.attributes,
			input: [
				['type', 'checkbox', 'radio'],
				['disabled', true, false],
				['checked', true, false],
				['name'],
				['id'],
				['value'],
			],
		};

		const finalSchema = options?.sanitizeSchemaExtension
			? deepmerge(schema, options.sanitizeSchemaExtension)
			: schema;

		const result = await unified()
			.use(remarkParse)
			.use(remarkGfm)
			.use(remarkDirective)
			.use(MarkdownService.remarkPlugin, options)
			.use(remarkHtml, { sanitize: finalSchema })
			.process(markdown);

		return {
			frontmatter,
			markdown,
			html: result.toString(),
		};
	}

	public async extractQuestionsFromMarkdown(markdownToConvert: string) {
		const { content: markdown } = parseYamlFrontmatter(markdownToConvert);

		const pipeline = unified()
			.use(remarkParse)
			.use(remarkGfm)
			.use(remarkDirective)
			.use(MarkdownService.remarkPlugin, {});

		pipeline.Compiler = MarkdownService.remarkExtractQuestions as never;

		try {
			const result = await pipeline.process(markdown);
			return result.result as MultiChoiceQuestion[];
		} catch (err) {
			// Wrap the error, otherwise it seems to be "empty"
			throw new Error((err as Error).message, { cause: err });
		}
	}

	public async extractInternallyLinkedArticleIdsFromMarkdown(markdownToConvert: string) {
		const { content: markdown } = parseYamlFrontmatter(markdownToConvert);

		const pipeline = unified()
			.use(remarkParse)
			.use(remarkGfm)
			.use(remarkDirective)
			.use(MarkdownService.remarkPlugin);

		pipeline.Compiler = MarkdownService.remarkExtractInternallyLinkedArticleIds as never;

		try {
			const result = await pipeline.process(markdown);
			return result.result as string[];
		} catch (err) {
			// Wrap the error, otherwise it seems to be "empty"
			throw new Error((err as Error).message, { cause: err });
		}
	}

	public static remarkExtractInternallyLinkedArticleIds(tree: Root) {
		const articleIds: Set<string> = new Set();

		visit(tree, (node: unknown) => {
			if (
				(nodeIsTextDirective(node) || nodeIsLeafDirective(node) || nodeIsContainerDirective(node)) &&
				node.name === 'article' &&
				node.children.length > 0
			) {
				const articleId = directiveLabelToText(node.children).trim();
				if (articleId) {
					articleIds.add(articleId);
				}
			}
		});

		return Array.from(articleIds);
	}

	public static remarkExtractQuestions(tree: Root) {
		const questions: MultiChoiceQuestion[] = [];

		visit(tree, 'containerDirective', (node: ContainerDirective) => {
			if (node.name === 'multiple-choice-question') {
				const question = parseNodeAsMultiChoiceQuestion(node);
				if (question) {
					// No de-duplication by question text: `remarkPlugin` runs first and fails the document on
					// a duplicate, so the branch that warned and overwrote here was unreachable. Keeping
					// every question is the safer behaviour if this is ever called on a raw tree.
					questions.push(question);
				}
			}
		});

		return questions;
	}

	public static remarkPlugin(options?: RemarkDirectiveOptions): Transformer<Root, Root> {
		return async (tree, file) => {
			const questions: Set<string> = new Set();

			visit(tree, (node, index?: number | null, parent?: unknown | null) => {
				if (nodeIsTextDirective(node) || nodeIsLeafDirective(node) || nodeIsContainerDirective(node)) {
					if (!node.data) {
						node.data = {};
					}
					if (!node.attributes) {
						node.attributes = {};
					}
					if (!node.children) {
						node.children = [];
					}

					const directiveNode = node as DirectiveNode;
					const parentNode = (parent as NodeWithChildren) ?? undefined;
					const childCountBeforeHandler = parentNode?.children.length;

					try {
						let directiveHandler: RemarkDirectiveHandler | null = null;
						if (node.name === 'youtube') {
							directiveHandler = options?.overrides?.youtube ?? MarkdownService.remarkYoutubeDirective;
						} else if (node.name === 'vimeo') {
							directiveHandler = options?.overrides?.vimeo ?? MarkdownService.remarkVimeoDirective;
						} else if (node.name === 'rollover') {
							directiveHandler = options?.overrides?.rollover ?? MarkdownService.remarkRolloverDirective;
						} else if (node.name === 'image') {
							directiveHandler = options?.overrides?.image ?? MarkdownService.remarkImageDirective;
						} else if (node.name === 'audio') {
							directiveHandler = options?.overrides?.audio ?? MarkdownService.remarkAudioDirective;
						} else if (node.name === 'article') {
							directiveHandler = options?.overrides?.article ?? MarkdownService.remarkArticleDirective;
						} else if (node.name === 'quiz') {
							directiveHandler = options?.overrides?.quiz ?? MarkdownService.remarkQuizDirective;
						} else if (node.name === 'resource') {
							directiveHandler = options?.overrides?.resource ?? MarkdownService.remarkResourceDirective;
						} else if (node.name === 'quote') {
							directiveHandler = options?.overrides?.quote ?? MarkdownService.remarkQuoteDirective;
						} else if (node.name === 'multiple-choice-question') {
							MarkdownService.remarkMultipleChoiceQuestionDirective(
								directiveNode,
								options,
								index ?? undefined,
								(parent as NodeWithChildren) ?? undefined,
							);
							const question = directiveNode.data.question as MultiChoiceQuestion | undefined;
							if (question) {
								if (questions.has(question.question)) {
									throw new Error(`Duplicate question found: '${question.question}'`);
								}
								questions.add(question.question);
								// Unset, as we don't care anymore and we don't want to keep a reference around
								delete directiveNode.data.question;
							}
						} else {
							// If we don't know how to handle the directive, we'll just insert the directive text
							(directiveNode as unknown as { value: string }).value = `:${directiveNode.name}`;
						}
						if (directiveHandler) {
							directiveHandler(
								directiveNode,
								options,
								index ?? undefined,
								(parent as NodeWithChildren) ?? undefined,
							);
						}
					} catch (err) {
						file.fail((err as Error).message, directiveNode);
					}

					// Several handlers remove the node from its parent when `stripMarkdown` is set. That
					// shortens the child list mid-walk, so the node now sitting at `index` is the one
					// `visit` is about to step over — leaving the *next* directive unprocessed and
					// stringified as its raw source (`::vimeo[...]`, `:::multiple-choice-question`).
					// Returning `index` makes the walk resume at that position instead of past it.
					//
					// Handlers that splice a replacement in are unaffected: the length is unchanged, so
					// `index + 1` is still the right next sibling.
					if (
						parentNode !== undefined &&
						childCountBeforeHandler !== undefined &&
						parentNode.children.length < childCountBeforeHandler &&
						index !== undefined &&
						index !== null
					) {
						return index;
					}
				}
			});
		};
	}

	public static remarkMultipleChoiceQuestionDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		const question = parseNodeAsMultiChoiceQuestion(node as ContainerDirective);

		if (!question) {
			return;
		}

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1);
			node.data.hName = 'span';
			node.data.hChildren = [];
			return;
		}

		node.data.question = question;
		node.data.hName = 'div';
		node.data.hProperties = {
			class: 'multiple-choice-question',
		};
		node.data.hChildren = [
			{
				type: 'element',
				tagName: 'fieldset',
				properties: {
					id: question.question,
				},
				children: [
					{
						type: 'element',
						tagName: 'legend',
						properties: {},
						children: [{ type: 'text', value: question.question }],
					},
					...question.options.map((o): Element => ({
						type: 'element',
						tagName: 'label',
						properties: {},
						children: [
							{
								type: 'element',
								tagName: 'input',
								properties: {
									type: 'radio',
									name: question.question,
									value: o.text,
									disabled: false,
								},
								children: [],
							},
							{ type: 'text', value: o.text },
						],
					})),
				],
			},
		];
	}

	public static remarkImageDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		let imgSrc = '';

		const child = node.children[0];
		if (child?.type == 'link') {
			imgSrc = child.url ?? '';
		} else {
			// The whole label, not just the first child: a src containing a colon-prefixed word splits
			// into several nodes.
			imgSrc = directiveLabelToText(node.children).trim();
		}
		// The label has been consumed as the src, so drop every node of it — not just the first.
		node.children = [];

		if (!imgSrc) {
			throw new Error('No source provided for image directive');
		}

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1);
			node.data.hName = 'span';
			node.data.hChildren = [];
			return;
		}

		node.data.hName = 'img';
		node.data.hProperties = {
			src: imgSrc,
			alt: node.attributes.alt ?? imgSrc,
			width: node.attributes?.width ?? undefined,
			height: node.attributes?.height ?? undefined,
		};
	}

	public static remarkAudioDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		let audioSrc = '';

		const child = node.children[0];
		if (child?.type == 'link') {
			audioSrc = child.url ?? '';
		} else {
			audioSrc = directiveLabelToText(node.children).trim();
		}
		node.children = [];

		if (!audioSrc) {
			throw new Error('No source provided for audio directive');
		}

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1);
			node.data.hName = 'span';
			node.data.hChildren = [];
			return;
		}

		node.data.hName = 'audio';
		node.data.hProperties = {
			controls: true,
		};
		node.data.hChildren = [
			{
				type: 'element',
				tagName: 'source',
				properties: {
					src: audioSrc,
					type: 'audio/mpeg',
				},
				children: [],
			},
			{
				type: 'text',
				value: 'Your browser does not support the audio element.',
			},
		];
	}

	public static remarkArticleDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		const id = directiveLabelToText(node.children).trim() || undefined;

		if (!id) {
			return;
		}

		const textValue =
			'alt' in node.attributes ? (node.attributes.alt ?? '') : Object.keys(node.attributes).join(' ');

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1, { type: 'text', value: textValue ?? '' });
			if (node.children[0].type == 'text') {
				node.children[0].value = textValue ?? '';
			}
			node.data.hName = 'span';
			node.data.hChildren = [{ type: 'text', value: textValue }];
			return;
		}

		node.data.hName = 'a';
		node.data.hProperties = {
			href: `/article/${id}${'slug' in node.attributes ? `/${node.attributes.slug}` : ''}`,
			target: '_blank',
			class: 'article-link',
			'data-article-id': id,
		};

		node.data.hChildren = [
			{
				type: 'text',
				value: textValue,
			},
		];
	}

	public static remarkQuizDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		const id = directiveLabelToText(node.children).trim() || undefined;

		if (!id) {
			return;
		}

		const textValue =
			'alt' in node.attributes ? (node.attributes.alt ?? '') : Object.keys(node.attributes).join(' ');

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1, { type: 'text', value: textValue ?? '' });
			node.data.hName = 'span';
			node.data.hChildren = [{ type: 'text', value: textValue }];
			return;
		}

		node.data.hName = 'a';
		node.data.hProperties = {
			href: `/quiz/${id}${'slug' in node.attributes ? `/${node.attributes.slug}` : ''}`,
			target: '_blank',
			class: 'quiz-link',
			'data-quiz-id': id,
		};
		node.data.hChildren = [
			{
				type: 'text',
				value: textValue,
			},
		];
	}

	public static remarkResourceDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		const id = directiveLabelToText(node.children).trim() || undefined;

		if (!id) {
			return;
		}

		const textValue =
			'alt' in node.attributes ? (node.attributes.alt ?? '') : Object.keys(node.attributes).join(' ');

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1, { type: 'text', value: textValue ?? '' });
			node.data.hName = 'span';
			node.data.hChildren = [{ type: 'text', value: textValue }];
			return;
		}

		node.data.hName = 'a';
		node.data.hProperties = {
			href: `/resource/${id}${'slug' in node.attributes ? `/${node.attributes.slug}` : ''}`,
			target: '_blank',
			class: 'resource-link',
			'data-resource-id': id,
		};
		node.data.hChildren = [
			{
				type: 'text',
				value: textValue,
			},
		];
	}

	public static remarkYoutubeDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		let id: string | undefined = undefined;
		if (node.attributes.id) {
			id = node.attributes.id;
		} else {
			const child = node.children[0];
			if (child && child.type === 'link' && child.url) {
				const url = new URL(child.url);
				if (url.hostname.endsWith('youtube.com')) {
					if (url.searchParams.has('v')) {
						id = url.searchParams.get('v') ?? undefined;
					}
				} else if (url.hostname.endsWith('youtu.be')) {
					id = url.pathname.substring(1);
				}
			}
		}

		if (id === undefined) {
			throw new Error('No ID provided for YouTube directive');
		}

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1);
			node.data.hName = 'span';
			node.data.hChildren = [];
			return;
		}

		node.data.hName = 'iframe';
		node.data.hProperties = {
			src: `https://www.youtube.com/embed/${id}`,
			title: 'YouTube video player',
			width: node.attributes.width || '640',
			height: node.attributes.height || node.attributes.width || '360',
			frameBorder: '0',
			allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
			allowFullScreen: true,
		};
	}

	public static remarkVimeoDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		let id: string | undefined = undefined;
		if (node.attributes.id) {
			id = node.attributes.id;
		} else {
			const child = node.children[0];
			if (child && child.type === 'link' && child.url) {
				const url = new URL(child.url);
				if (url.hostname === 'vimeo.com') {
					const match = url.pathname.match(/\/(\d+)\/?([0-9a-zA-Z]+)?/);
					if (match) {
						id = match[1];
						// The hash group is optional, so test whether it matched. `match.length` is always
						// 3 for this pattern and testing it instead appended the string "undefined".
						if (match[2]) {
							id += `?h=${match[2]}`;
						}
					}
				}
			}
		}

		if (id === undefined) {
			throw new Error('No ID provided for Vimeo directive');
		}

		if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
			parent.children.splice(index, 1);
			node.data.hName = 'span';
			node.data.hChildren = [];
			return;
		}

		node.data.hName = 'iframe';
		node.data.hProperties = {
			src: `https://player.vimeo.com/video/${id}`,
			title: 'video player',
			width: node.attributes.width || '640',
			height: node.attributes.height || node.attributes.width || '360',
			frameBorder: '0',
			allowFullScreen: true,
		};
	}

	public static remarkRolloverDirective(
		node: DirectiveNode,
		options?: RemarkDirectiveOptions,
		index?: number,
		parent?: NodeWithChildren,
	) {
		const matches = directiveLabelToText(node.children).match(/(.+?)\((.+)\)/);

		if (matches && matches.length === 3) {
			const [, text, definition] = matches;

			if (options?.stripMarkdown && index !== undefined && index >= 0 && parent) {
				parent.children.splice(index, 1, { type: 'text', value: text });
				node.data.hName = 'span';
				node.data.hChildren = [{ type: 'text', value: text }];
				return;
			}

			node.data.hName = 'a';
			node.data.hProperties = {
				href: '#',
				class: 'rollover',
				'data-tippy-content': definition,
			};
			// Replaces the children outright rather than editing the first one, which is only the start of
			// the term when the label was split across several nodes.
			node.children = [{ type: 'text', value: text }];
			return;
		}

		throw new Error('Rollover directive is not valid');
	}

	public static remarkQuoteDirective(node: DirectiveNode) {
		if (!nodeIsContainerDirective(node)) {
			throw new Error('Quote directive must be written as a container directive (:::quote)');
		}

		node.data.hName = 'blockquote';
		node.data.hProperties = { class: 'pull-quote' };
	}
}

export type MultiChoiceQuestionOption = {
	text: string;
	isAnswer: boolean;
	explanation?: string;
};

export type MultiChoiceQuestion = {
	question: string;
	options: MultiChoiceQuestionOption[];
};

export const parseNodeAsMultiChoiceQuestion = (node: ContainerDirective): MultiChoiceQuestion | null => {
	let question: string | undefined = undefined;
	let options: MultiChoiceQuestionOption[] | undefined = undefined;

	if (node.children.length >= 2) {
		const questionNode = node.children[0] as Paragraph;
		// The options are whichever paragraph holds `:option` directives, rather than always the second
		// child: a question with any extra content — a trailing note, a second prompt paragraph — used to
		// be discarded in full and silently vanish from the quiz. Falling back to the second child keeps
		// a question whose options paragraph holds none, which callers rely on to report it as unanswered.
		const optionsNode = (node.children.find(
			(child) =>
				child.type === 'paragraph' &&
				child.children.some((c) => nodeIsTextDirective(c) && (c as TextDirective).name === 'option'),
		) ?? node.children[1]) as Paragraph;

		if (questionNode.children.length && questionNode.children[0].type === 'text') {
			question = questionNode.children[0].value;
		}

		if (optionsNode.type === 'paragraph' && optionsNode.children.length) {
			options = optionsNode.children
				.filter((c) => nodeIsTextDirective(c) && (c as TextDirective).name == 'option')
				.map((c) => {
					const option = c as unknown as TextDirective;
					const explanation = option.attributes?.explanation ?? undefined;
					const isAnswer =
						option.attributes &&
						typeof option.attributes.answer !== 'undefined' &&
						option.attributes.answer !== 'false'
							? true
							: false;
					const text = option.children.length ? `${(option.children[0] as Literal).value}` : '';
					return {
						text,
						isAnswer,
						explanation,
					} as MultiChoiceQuestionOption;
				});
		}

		return {
			question: question ?? '',
			options: options ?? [],
		};
	}
	return null;
};

export function useMarkdownService() {
	return useMemo(() => new MarkdownService(), []);
}

export function nodeIsTextDirective(node: unknown): node is TextDirective {
	return (node as TextDirective).type === 'textDirective';
}

export function nodeIsLeafDirective(node: unknown): node is LeafDirective {
	return (node as LeafDirective).type === 'leafDirective';
}

export function nodeIsContainerDirective(node: unknown): node is ContainerDirective {
	return (node as ContainerDirective).type === 'containerDirective';
}
